/* import __COLOCATED_TEMPLATE__ from './connect-cta.hbs'; */
/* RESPONSIBLE TEAM: team-conversational-knowledge */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'ember-intl/services/intl';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';

interface Args {
  onClick: () => void;
  page: 'suggestions' | 'analyze';
  platform: 'zendesk' | 'salesforce';
}

export default class ConnectCta extends Component<Args> {
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get platform() {
    return this.args.platform;
  }

  get isPlatformConnected() {
    return this.platform === 'salesforce'
      ? this.finStandaloneService.isSalesforceConnected
      : this.finStandaloneService.isZendeskConnected;
  }

  get titleIcon() {
    return this.isSuggestionsPage ? 'stars' : 'sync';
  }

  get isSuggestionsPage() {
    return this.args.page === 'suggestions';
  }

  get title() {
    if (this.isSuggestionsPage) {
      return this.intl.t('ai-content-library.content-reviews.empty-state.ai-insights.title');
    }

    if (this.zendesk) {
      if (this.isPlatformConnected) {
        return this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.zendesk.conversation-syncing-not-enabled-title',
        );
      } else {
        return this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.zendesk.not-connected-title',
        );
      }
    }

    if (this.salesforce) {
      if (this.isPlatformConnected) {
        return this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.salesforce.conversation-syncing-not-enabled-title',
        );
      } else {
        return this.intl.t(
          'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.salesforce.not-connected-title',
        );
      }
    }

    // We should never get here
    return this.intl.t(
      'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.not-connected-title',
    );
  }

  get description() {
    if (this.isSuggestionsPage) {
      if (this.zendesk) {
        return this.intl.t(
          'ai-content-library.content-reviews.empty-state.ai-insights.zendesk.not-connected-description',
        );
      } else {
        return this.intl.t(
          'ai-content-library.content-reviews.empty-state.ai-insights.salesforce.not-connected-description',
        );
      }
    }

    if (this.isPlatformConnected) {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.conversation-syncing-not-enabled-description',
      );
    } else {
      return this.intl.t(
        'components.reporting.automation.ai-insights.fin-insights.platform-not-connected.not-connected-description',
      );
    }
  }

  get buttonText() {
    if (this.isPlatformConnected) {
      if (this.zendesk) {
        return this.intl.t('standalone.zendesk.settings.connect.manage-button-label');
      } else {
        return this.intl.t('standalone.salesforce.setup.connect.manage-button-label');
      }
    }

    if (this.zendesk) {
      return this.intl.t('standalone.zendesk.settings.connect.connect-button-label');
    } else {
      return this.intl.t('standalone.salesforce.setup.connect.connect-button-label');
    }
  }

  get platformIcon() {
    if (this.zendesk) {
      return 'zendesk';
    } else {
      return 'salesforce';
    }
  }

  get zendesk() {
    return this.args.platform === 'zendesk';
  }

  get salesforce() {
    return this.args.platform === 'salesforce';
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Common::ConnectCta': typeof ConnectCta;
    'standalone/common/connect-cta': typeof ConnectCta;
  }
}
